<template>
  <nav>
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType" 
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />
        
    <v-row
      align="center"
      class="pt-md-10"
    >
      <v-col
        cols="12"
        md="4"
        lg="3"
        xl="2"
        offset-md="1"
        offset-lg="2"
        offset-xl="3"
        class="text-center"
      >   
        <v-img
          v-if="!loadingData"
          data-testid="user-image"
          contain
          :src="avatar != null ? avatar : require('../../assets/user_images/profile-avatar.png')"
          width="160"
          height="160"
          class="rounded-circle mx-auto"
          :class="avatar != null ? '' : 'default'"
        >
          <div class="center-button-on-image">
            <v-menu
              offset-y
              flat
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :loading="loading"
                  :disabled="loading"
                  data-testid="edit-user-image-button"
                  color="accent"
                  class="rounded-xxl text-none"
                  elevation="0"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ $t('editImage') }}
                </v-btn>
              </template>
              <v-list data-testid="menu-list">
                <v-list-item
                  data-testid="show-uploader-item"
                  router
                  @click="showUploadAvatarImage"
                >
                  <v-list-item-title>{{ $t('uploadNew') }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="avatar != null"
                  data-testid="remove-user-image-item"
                  router 
                  @click.stop="
                    dialogVisible=true, 
                    confirmationDialogTitle=$t('confirmationDialogTitleDeleteAvatar'), 
                    confirmationDialogText=$t('confirmationDialogTextDeleteAvatar'), 
                    confirmationDialogSubmitBtn=$t('delete')
                    confirmationDialogCancelBtn=$t('no')"
                >
                  <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-file-input 
              ref="file_input"
              v-model="imageData"
              accept="image/png, image/jpeg"
              prepend-icon="mdi-camera"
              data-testid="file-input"
              class="d-none"
              @change="uploadAvatarImage"
            />
          </div>
        </v-img>
      </v-col>
      <v-col
        md="6"
        lg="5"
        xl="4"
      >
        <p class="text-h5 font-weight-bold text-center text-md-left">
          {{ $t('profileWelcomeTitle', {'name': first_name }) }}
        </p>
        <p class="text-body-1 d-none d-md-block">
          {{ $t('profileWelcomeMessage') }}
        </p>
      </v-col>
    </v-row>
    <v-row class="pt-md-10">
      <v-col
        cols="10"
        class="mx-auto"
      >
        <v-row>
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card
                  data-testid="first-last-name-card"
                  rounded="xxl"
                  max-width="400"
                  class="mx-auto pa-4 pb-8"
                  :elevation="hover ? 8 : 5"
                >
                  <EditFirstLastName 
                    @proceedSubmit="showUpdateUserMessage"
                    @proceedFirstLastName="updateUserData"
                  />
                  <v-card-title class="font-weight-bold no-break">
                    {{ $t('profileCardNameTitle') }}
                  </v-card-title>
                  <v-card-text
                    v-if="error == ''"
                    class="text-body-1"
                  >
                    {{ first_name }} {{ last_name }}
                  </v-card-text>
                  <v-card-text
                    v-else
                    class="text-body-1 error--text"
                  >
                    {{ error }}
                  </v-card-text>
                </v-card>
              </template>
            </v-hover>
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card
                  data-testid="email-card"
                  rounded="xxl"
                  max-width="400"
                  class="mx-auto pa-4 pb-8"
                  :elevation="hover ? 8 : 5"
                >
                  <EditEmailAddress 
                    :new-email-temp="new_email_temp"
                    @proceedSubmit="showUpdateUserMessage"
                    @proceedEmail="updateUserEmailCard"
                  />
                  <v-card-title class="font-weight-bold no-break">
                    {{ $t('profileCardEmailTitle') }}                                         
                    <v-chip
                      v-if="new_email_temp != null"
                      x-small
                      data-testid="email-notconfirmed-chip"
                      class="ml-3"
                      color="warning"
                    >
                      {{ $t('notConfirmed') }}
                    </v-chip><br>
                  </v-card-title>
                  <v-card-text
                    v-if="error == ''"
                    class="text-body-1"
                    :class="{'pb-0': new_email_temp != null}"
                  >
                    <span
                      v-if="new_email_temp != null"
                      class="mr-2"
                    >{{ new_email_temp }}</span>
                    <span v-else>{{ email }}</span><br>
                    <span
                      v-if="new_email_temp != null"
                      data-testid="email-notconfirmed-span"
                      class="text-caption"
                    >{{ $t('editUserEmailNotConfirmedText') }}</span>
                  </v-card-text>
                  <v-card-text
                    v-else
                    class="text-body-1 error--text"
                  >
                    {{ error }}
                  </v-card-text>
                  <v-card-actions
                    v-if="new_email_temp != null"
                    class="pt-0"
                  >
                    <v-spacer />
                    <v-btn
                      right
                      data-testid="submit-button"
                      color="error"
                      class="rounded-xxl text-none text-caption px-4" 
                      @click.stop="
                        dialogDismissEmailChangeVisible=true, 
                        confirmationDialogTitle=$t('confirmationDialogTitleDismissEmailChange'), 
                        confirmationDialogText=$t('confirmationDialogTextDismissEmailChange'), 
                        confirmationDialogSubmitBtn=$t('yes')
                        confirmationDialogCancelBtn=$t('no')"
                    >
                      {{ $t('dismissChange') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </template>
            </v-hover>
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card
                  data-testid="password-card"
                  rounded="xxl"
                  max-width="400"
                  class="mx-auto pa-4 pb-8"
                  :elevation="hover ? 8 : 5"
                >
                  <EditPassword
                    @proceedSubmit="showUpdateUserMessage"
                  />
                  <v-card-title class="font-weight-bold no-break">
                    {{ $t('profileCardPasswordTitle') }}
                  </v-card-title>
                  <v-card-text
                    v-if="error == ''"
                    class="text-body-1"
                  >
                    {{ password }}
                  </v-card-text>
                  <v-card-text
                    v-else
                    class="text-body-1 error--text"
                  >
                    {{ error }}
                  </v-card-text>
                </v-card>
              </template>
            </v-hover>
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card
                  data-testid="language-card"
                  rounded="xxl"
                  max-width="400"
                  class="mx-auto pa-4 pb-8"
                  :elevation="hover ? 8 : 5"
                >
                  <ChangeUserLanguage
                    @proceedSubmit="showUpdateUserMessage"
                    @proceedPreferedLanguage="updateUserLanguage"
                  />
                  <v-card-title class="font-weight-bold no-break">
                    {{ $t('profileCardLanguageTitle') }}
                  </v-card-title>
                  <v-card-text
                    v-if="error == ''"
                    data-testid="language-card-language"
                    class="text-body-1"
                  >
                    {{ languageReadable }}
                  </v-card-text>
                  <v-card-text
                    v-else
                    class="text-body-1 error--text"
                  >
                    {{ error }}
                  </v-card-text>
                </v-card>
              </template>
            </v-hover>
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card
                  data-testid="delete-account-card"
                  rounded="xxl"
                  max-width="400"
                  class="mx-auto pa-4  pb-8"
                  :elevation="hover ? 8 : 5"
                >
                  <v-btn
                    data-testid="delete-button"
                    color="error"
                    icon
                    absolute
                    right
                    top
                    class="mt-n2 mr-n2"
                    @click="checkUserBeforeDeletion"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                  <v-card-title class="font-weight-bold no-break">
                    {{ $t('deleteAccount') }}
                  </v-card-title>
                  <v-card-text class="text-body-1">
                    {{ $t('deleteAccountTextCard') }}
                  </v-card-text>
                </v-card>
              </template>
            </v-hover>
          </v-col>
          <v-col
            cols="12"
            md="6"
            lg="4"
          >
            <v-hover>
              <template v-slot:default="{ hover }">
                <v-card
                  data-testid="personal-data-card"
                  rounded="xxl"
                  max-width="400"
                  class="mx-auto pa-4  pb-8"
                  :elevation="hover ? 8 : 5"
                >
                  <v-card-title class="font-weight-bold no-break">
                    {{ $t('personalData') }}
                  </v-card-title>
                  <v-card-text class="text-body-1">
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                      ref="link"
                      v-html="$t('personalDataText', {
                        'privacy-policy': `<a target='_blank' href='/privacy-policy'>${ $t('privacyPolicy') }</a>`
                      })" 
                    />
                  </v-card-text>
                </v-card>
              </template>
            </v-hover>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <ConfirmationDialog 
      v-if="dialogVisible"
      ref="confirmationDialog"
      :visible="dialogVisible" 
      :cd-title="confirmationDialogTitle" 
      :cd-text="confirmationDialogText" 
      :cd-submit-btn="confirmationDialogSubmitBtn" 
      :cd-cancel-btn="confirmationDialogCancelBtn" 
      data-testid="confirmation-dialog" 
      @close="dialogVisible=false"
      @proceedSubmit="removeAvatarImage"
    />
    
    <InfoDialog 
      ref="infoDialogDeleteAccount"
      :visible="dialogVisibleDeleteAccount" 
      :cd-title="infoDialogTitle" 
      :cd-text="infoDialogText" 
      data-testid="info-dialog-delete-account" 
      @close="dialogVisibleDeleteAccount=false" 
    />
    <ConfirmationDialog 
      v-if="dialogDismissEmailChangeVisible"
      ref="confirmationDialogDismissEmailChange"
      :visible="dialogDismissEmailChangeVisible" 
      :cd-title="confirmationDialogTitle" 
      :cd-text="confirmationDialogText" 
      :cd-submit-btn="confirmationDialogSubmitBtn" 
      :cd-cancel-btn="confirmationDialogCancelBtn" 
      data-testid="confirmation-dialog-dismiss-email-change" 
      @close="dialogDismissEmailChangeVisible=false"
      @proceedSubmit="dismissEmailChange"
    />
  </nav>
</template>
<script>
import i18n from '../../plugins/i18n'
import { getUser, setAvatar, deleteAvatar, verifyEmailChange, dismissEmailChange,checkUserBeforeDeletion } from '../../api/userApi'
import {eventBus} from '../../eventbus'
//import moment from 'moment'
import ConfirmationDialog from '../../components/ConfirmationDialog';
import InfoDialog from '../../components/InfoDialog';
import EditFirstLastName from './EditFirstLastName'
import EditEmailAddress from './EditEmailAddress'
import EditPassword from './EditPassword'
import ChangeUserLanguage from './ChangeUserLanguage'
import AlertMessages  from '../../components/AlertMessages'

export default {
    components: { 
        ConfirmationDialog,
        EditFirstLastName,
        EditEmailAddress,
        EditPassword,
        ChangeUserLanguage,
        AlertMessages,
        InfoDialog
    },
    data(){
        return{
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            language: localStorage.user ? JSON.parse(localStorage.user).language : '',
            languageReadable: 'english',
            avatar: null,
            error: '',
            userId: window.$cookies.get('id'),
            imageData: null,
            loading: false,
            alertType: '',
            alertMsg: '',
            alertValue:false,
            dialogVisible: false,
            dialogDismissEmailChangeVisible: false,
            confirmationDialogTitle: '',
            confirmationDialogText: '',
            confirmationDialogCancelBtn: '',
            confirmationDialogSubmitBtn: '',
            dialog: false,
            new_email_temp: localStorage.user ? JSON.parse(localStorage.user).new_email_temp : null,
            loadingData: false,
            dialogVisibleDeleteAccount: false,
            infoDialogTitle: '',
            infoDialogText: '',
            code: '',
        }
    },
    async created(){
        switch(this.language) {
            case 'en':
                this.languageReadable = i18n.t('english')
                break;
            case 'sv':
                this.languageReadable = i18n.t('swedish')
                break;
            case 'mk':
                this.languageReadable = i18n.t('macedonian')
                break;
            default:
                this.languageReadable = i18n.t('english')
        }
        
        this.loadingData = true
        if(this.$route.query.new_email_confirmation_url){
            try {
                const response = await verifyEmailChange(this.$route.query.new_email_confirmation_url, this.$route.query.signature)
                if(response.status == 200) {
                    this.alertType = 'success'
                    if(response.data.meta.code == 'newEmailConfirmed'){
                        this.alertValue = true
                        this.alertMsg =  i18n.t('editUserSuccess')
                        this.new_email_temp = null
                        localStorage.user = JSON.stringify(response.data.data.attributes)
                    }
                }
            } catch (e) {
                this.alertValue = true
                this.alertType = 'error'
                this.alertMsg =  i18n.t('serverError')
            }
        }
        try {
            const response = await getUser(this.userId) 
            if(response.status == 200) {
                this.first_name = response.data.data.attributes.first_name
                this.last_name = response.data.data.attributes.last_name
                this.email = response.data.data.attributes.email
                this.password = '**********'
                this.new_email_temp = response.data.data.attributes.new_email_temp
                this.avatar = response.data.data.attributes.avatar_thumb
                localStorage.user = JSON.stringify(response.data.data.attributes)
            }
        } catch (e) {
            if(e.response){
                this.error= i18n.t('dataNotRetrievedError')
            }
        }
        this.loadingData = false
    },
    methods: {
        showUploadAvatarImage(){
            this.$refs.file_input.$refs.input.click()
        },
        confirmDeleteAccount(){
            this.$router.push({path: '/profile/delete' })
        },
        async removeAvatarImage(){
            this.dialogVisible = false
            this.loading = true
            try {
                const response = await deleteAvatar(this.userId) 
                if(response.status == 200) {
                    //no message to be shown
                    localStorage.user = JSON.stringify(response.data.data.attributes)
                    this.avatar = null
                    eventBus.$emit('changeAvatar')
                    this.alertMsg = ''
                }
            } catch (e) {
                this.alertType = 'error'
                this.alertMsg =  i18n.t('serverError')
                this.alertValue = true
            }
            this.loading = false
        },
        async uploadAvatarImage(){
            if(this.imageData){
                if(this.imageData.size > 3145728){
                    this.alertType = 'error'
                    this.alertMsg =  i18n.t('uploadAvatarError')
                    this.alertValue = true
                }else {
                    this.loading = true
                    try {
                        let data = new FormData();
                        data.append('avatar', this.imageData);
                        
                        const response = await setAvatar(this.userId, data) 
                        localStorage.user = JSON.stringify(response.data.data.attributes)
                        this.avatar = localStorage.user ? JSON.parse(localStorage.user).avatar_thumb : null
                        eventBus.$emit('changeAvatar')

                        if(response.status == 200) {
                            this.loading = false
                            this.alertMsg = ''
                            //no message to be shown
                        }
                    } catch (e) {
                        if(e.response){
                            this.alertValue = true
                            if(e.response.status == 422){
                                this.alertType = 'error'
                                this.alertMsg =  i18n.t('uploadAvatarError')
                            }else if (e.response.status == 403){
                                for(let i = 0; i < e.response.data.errors.length; i++){
                                    if(e.response.data.errors[i].detail == 'Storage is full'){
                                        this.alertType = "error";
                                        this.alertMsg =  i18n.t('storageFullError')
                                    }
                                    else {
                                        this.$router.push({name: 'PageAccessDenied'})
                                    }
                                }
                            } else {
                                this.alertType = 'error'
                                this.alertMsg =  i18n.t('serverError')
                            }
                        }
                        this.loading = false
                    }
                }
            }
        },
        showUpdateUserMessage(msg, type){
            if(msg) {
                this.alertType= type
                this.alertMsg = msg
                this.alertValue = true
            }
        },
        updateUserEmailCard(email){
            this.new_email_temp = email
            //localStorage.user = JSON.stringify(data)
        },
        updateUserData(data){
            this.first_name = data.first_name
            this.last_name = data.last_name
            localStorage.user = JSON.stringify(data)
            eventBus.$emit('changeUserData')
        },
        updateUserLanguage(data){
            localStorage.user = JSON.stringify(data)
            this.language = data.language
            this.languageReadable = this.language == 'en' ? i18n.t('english') : this.language == 'sv' ? i18n.t('swedish') : this.language == 'mk' ? i18n.t('macedonian') : i18n.t('english')
            eventBus.$emit('changeUserLanguage')
        },
        async checkUserBeforeDeletion(){
            try {
                const response = await checkUserBeforeDeletion() 
                if (response.status == 200) {
                    this.code = response.data.meta.code
                    if (this.code != '') {
                      this.confirmDeleteAccount()
                    } else {
                        this.dialogVisibleDeleteAccount = true
                        this.infoDialogTitle = i18n.t('infoDialogTitleDeleteAccount')
                        this.infoDialogText = i18n.t('infoDialogTextDeleteAccount')
                        this.confirmationDialogSubmitBtn = i18n.t('ok')
                    }
                }
            } catch (e) {
                if(e.response){
                    if (e.response.status == 403 ){ 
                        this.dialogVisibleDeleteAccount = true
                        this.infoDialogTitle = i18n.t('infoDialogTitleDeleteAccount')
                        this.infoDialogText = i18n.t('infoDialogTextDeleteAccount')
                        this.confirmationDialogSubmitBtn = i18n.t('ok')
                    }
                    else{
                      this.alertValue = true
                      this.alertType = 'error'
                      this.alertMsg =  i18n.t('serverError')
                    }
                }
            }
        },
        async dismissEmailChange(){
            this.dialogDismissEmailChangeVisible = false
            try {
                const response = await dismissEmailChange(this.userId) 
                if(response.status == 200) {
                    this.alertType = 'success'
                    this.alertMsg = i18n.t('editUserEmailDismissChange')
                    this.alertValue = true
                    this.new_email_temp = null
                    localStorage.user = JSON.stringify(response.data.data.attributes)
                }
            } catch (e) {
                this.alertValue = true
                this.alertType = 'error'
                this.alertMsg =  i18n.t('serverError')
            }
        }
    },
}
</script>